import { FC, useState } from 'react';

import { AddDocumentIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../../components/hover-card/index';
import { PaginationController } from '../../../../components/pagination-controller/index';
import { Company, EventHooks, useFormat } from '../../../../hooks/index';
import { EventTypeBackend } from '../../../../types/events.types';
import { getConversionMethodTitle, Safe } from '../../../../types/safes.types';
import { getS3FileOriginalName } from '../../../../utils/get-s3file-original-name.util';
import { toMathRound } from '../../../../utils/to-math-round.util';
import { CardProps } from './transaction-card-variants.types';
import { TransactionOverviewItem } from './transaction-overview-item';
import { TransactionTitleItem } from './transaction-title-item';

export const SafeConversion: FC<CardProps> = ({ event, companyId, openFileUploadModal }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { format } = useFormat();
  const { selectedCompany } = Company.useSelected();
  const { items, totalPages } = EventHooks.useTransactions<Safe>({
    eventId: event.eventId,
    eventType: EventTypeBackend.SAFE_CONVERSION,
    companyId: companyId,
    currentPage,
  });

  if (event.type !== EventTypeBackend.SAFE_CONVERSION) return <></>;
  return (
    <>
      {items.map(
        (
          {
            conversionDate,
            stakeholder,
            investment,
            convertedShares,
            conversionMethod,
            capType,
            conversion: { valuation, postConversionSharePrice: eventSharePrice } = {},
            filesLinks,
          },
          i,
        ) => (
          <div
            className="flex flex-col divide-y divide-gray-100 rounded-md shadow-sm"
            key={i + stakeholder.id}
          >
            <TransactionTitleItem
              createdAt={format(conversionDate, 'dd/MM/yyyy')}
              name={stakeholder?.fullName}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="STAKEHOLDER" />}
              value={stakeholder.fullName}
            />

            <TransactionOverviewItem
              title={<AppFormattedMessage id="INVESTMENT" />}
              value={investment.toLocaleString('en-US')}
            />
            {conversionMethod && (
              <TransactionOverviewItem
                title={<AppFormattedMessage id="CONVERSION_METHOD" />}
                value={
                  <AppFormattedMessage id={getConversionMethodTitle(conversionMethod, capType)} />
                }
              />
            )}
            <TransactionOverviewItem
              className="uppercase"
              title={<AppFormattedMessage id="LINKED_EVENT" />}
              value={valuation?.name}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="SHARE_PRICE" />}
              value={`${selectedCompany?.currency?.symbol} ${toMathRound(eventSharePrice)?.toLocaleString('en-US')}`}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="NO_OF_SHARES" />}
              value={convertedShares?.toLocaleString('en-US')}
            />

            {filesLinks.length > 0 && (
              <TransactionOverviewItem
                title={<AppFormattedMessage id="DOCUMENTS" />}
                value={
                  <HoverCard>
                    <HoverCardTrigger onClick={() => openFileUploadModal(filesLinks)}>
                      <AddDocumentIcon className="cursor-pointer" />
                    </HoverCardTrigger>

                    <HoverCardContent
                      className="max-w-[360px] rounded-lg bg-[#101828] px-3 py-2"
                      side="top"
                    >
                      <span className="block w-full truncate p-0 text-label-md font-[550] text-white">
                        {filesLinks.length === 1
                          ? getS3FileOriginalName(filesLinks[0])
                          : `Download ${filesLinks.length} files`}
                      </span>
                    </HoverCardContent>
                  </HoverCard>
                }
              />
            )}
          </div>
        ),
      )}
      <PaginationController
        className="mt-6 shrink-0"
        currentPage={currentPage}
        onClick={setCurrentPage}
        totalPages={totalPages}
      />
    </>
  );
};
